import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { bookingsAPI, providersAPI } from '../lib/api';
import { extUrl } from '../lib/url';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  CalendarDays, Clock, User, CheckCircle, XCircle,
  Plus, Search, ChevronLeft, ChevronRight, ExternalLink, Star, Briefcase,
  Heart, Plane, Stethoscope, GraduationCap, Wrench, Home, Scale,
  Scissors, Camera, Music, Palette, Dumbbell, UtensilsCrossed, PlusCircle, Loader2, X
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, setHours, setMinutes, isBefore, isToday, parseISO } from 'date-fns';
import usePageTitle from '../hooks/usePageTitle';

const TIME_SLOTS = [];
for (let h = 9; h < 17; h++) {
  for (let m = 0; m < 60; m += 30) {
    TIME_SLOTS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
}

const SERVICE_AREAS = [
  { value: 'career_coach', label: 'Career Coach', icon: Star, color: 'bg-amber-50 text-amber-600' },
  { value: 'real_estate', label: 'Realtor', icon: Home, color: 'bg-blue-50 text-blue-600' },
  { value: 'financial', label: 'Financial Advisor', icon: Briefcase, color: 'bg-emerald-50 text-emerald-600' },
  { value: 'legal', label: 'Attorney', icon: Scale, color: 'bg-purple-50 text-purple-600' },
  { value: 'healthcare', label: 'Healthcare', icon: Stethoscope, color: 'bg-red-50 text-red-600' },
  { value: 'therapist', label: 'Therapist', icon: Heart, color: 'bg-pink-50 text-pink-600' },
  { value: 'tutor', label: 'Tutor', icon: GraduationCap, color: 'bg-indigo-50 text-indigo-600' },
  { value: 'career', label: 'Career Services', icon: Briefcase, color: 'bg-slate-50 text-slate-600' },
  { value: 'travel', label: 'Travel Agent', icon: Plane, color: 'bg-sky-50 text-sky-600' },
  { value: 'home_repair', label: 'Home Repair', icon: Wrench, color: 'bg-orange-50 text-orange-600' },
  { value: 'hair_stylist', label: 'Hair Stylist', icon: Scissors, color: 'bg-fuchsia-50 text-fuchsia-600' },
  { value: 'photographer', label: 'Photographer', icon: Camera, color: 'bg-cyan-50 text-cyan-600' },
  { value: 'personal_trainer', label: 'Personal Trainer', icon: Dumbbell, color: 'bg-lime-50 text-lime-600' },
  { value: 'caterer', label: 'Caterer', icon: UtensilsCrossed, color: 'bg-yellow-50 text-yellow-600' },
  { value: 'music_instructor', label: 'Music Instructor', icon: Music, color: 'bg-violet-50 text-violet-600' },
  { value: 'graphic_designer', label: 'Graphic Designer', icon: Palette, color: 'bg-teal-50 text-teal-600' },
];

const SERVICE_TYPES = [
  { value: 'all', label: 'All Services', icon: Briefcase },
  ...SERVICE_AREAS,
];

const Bookings = () => {
  usePageTitle('Bookings');
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('my-bookings');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [bookingDialog, setBookingDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [bookingNotes, setBookingNotes] = useState('');
  const [bookingType, setBookingType] = useState('general');
  const [providerSearch, setProviderSearch] = useState('');
  const [availability, setAvailability] = useState(null);
  const [creating, setCreating] = useState(false);
  const [selectedServiceType, setSelectedServiceType] = useState('all');
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [showSuggestDialog, setShowSuggestDialog] = useState(false);
  const [suggestedService, setSuggestedService] = useState('');
  const [suggestingService, setSuggestingService] = useState(false);

  // Build merged service areas: defaults + any custom from DB
  const allServiceAreas = (() => {
    const defaultValues = new Set(SERVICE_AREAS.map(s => s.value));
    const extras = dynamicCategories
      .filter(val => val && !defaultValues.has(val))
      .map(val => ({
        value: val,
        label: val.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        icon: Briefcase,
        color: 'bg-slate-50 text-slate-600',
      }));
    return [...SERVICE_AREAS, ...extras];
  })();

  const allServiceTypes = [{ value: 'all', label: 'All Services', icon: Briefcase }, ...allServiceAreas];

  useEffect(() => {
    fetchBookings();
    fetchProviders();
    providersAPI.getCategories()
      .then(res => setDynamicCategories(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  // Auto-open book-new tab and select provider from URL params
  useEffect(() => {
    const providerId = searchParams.get('provider');
    if (providerId && providers.length > 0) {
      setActiveTab('book-new');
      const provider = providers.find(p => p.provider_id === providerId);
      if (provider) {
        handleSelectProvider(provider);
      }
    }
  }, [searchParams, providers]);

  const fetchBookings = async () => {
    try {
      const res = await bookingsAPI.list();
      setBookings(res.data);
    } catch (e) {
      console.error('Failed to fetch bookings:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const res = await providersAPI.list({ limit: 50 });
      setProviders(res.data);
    } catch (e) {
      console.error('Failed to fetch providers:', e);
    }
  };

  const fetchAvailability = async (providerId) => {
    try {
      const res = await bookingsAPI.getProviderAvailability(providerId);
      setAvailability(res.data);
    } catch (e) {
      console.error('Failed to fetch availability:', e);
    }
  };

  const handleSelectProvider = (provider) => {
    setSelectedProvider(provider);
    fetchAvailability(provider.provider_id);
    setBookingDialog(true);
  };

  const handleBook = async () => {
    if (!selectedProvider || !selectedTime || !selectedDate) return;
    setCreating(true);

    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const startTime = `${dateStr}T${selectedTime}:00`;

    try {
      const res = await bookingsAPI.create({
        provider_id: selectedProvider.provider_id,
        start_time: startTime,
        duration: 30,
        consultation_type: bookingType,
        notes: bookingNotes
      });
      setConfirmedBooking({ ...res.data, provider_name: selectedProvider.name, start_time: startTime });
      toast.success('Booking requested!');
      setBookingDialog(false);
      setSelectedProvider(null);
      setSelectedTime('');
      setBookingNotes('');
      fetchBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create booking');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateBooking = async (bookingId, status) => {
    try {
      await bookingsAPI.update(bookingId, { status });
      toast.success(`Booking ${status}`);
      fetchBookings();
    } catch (e) {
      toast.error('Failed to update booking');
    }
  };

  const getBookedSlots = () => {
    if (!availability?.bookings || !selectedDate) return [];
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    return availability.bookings
      .filter(b => b.start_time?.startsWith(dateStr))
      .map(b => b.start_time?.split('T')[1]?.slice(0, 5));
  };

  const getDayName = (date) => format(date, 'EEEE').toLowerCase();

  const getAvailableSlots = () => {
    if (!availability?.availability?.weekly_hours) return TIME_SLOTS;
    const dayName = getDayName(selectedDate);
    const dayHours = availability.availability.weekly_hours[dayName];
    if (!dayHours || dayHours.length === 0) return [];

    const bookedSlots = getBookedSlots();
    return TIME_SLOTS.filter(slot => {
      if (bookedSlots.includes(slot)) return false;
      return dayHours.some(h => slot >= h.start && slot < h.end);
    });
  };

  const handleSuggestService = () => {
    const name = suggestedService.trim();
    if (!name) return;
    setSuggestingService(true);
    // Add it locally immediately so user sees feedback
    const value = name.toLowerCase().replace(/\s+/g, '_');
    const existing = allServiceAreas.find(s => s.value === value);
    if (existing) {
      toast.info(`"${name}" is already listed!`);
      setSuggestingService(false);
      return;
    }
    setDynamicCategories(prev => [...prev, value]);
    toast.success(`"${name}" added! Browse providers or become one in the Service Directory.`);
    setSuggestedService('');
    setShowSuggestDialog(false);
    setSuggestingService(false);
  };

  const handleServiceAreaClick = (value) => {
    setSelectedServiceType(value);
    setActiveTab('book-new');
  };

  const getGoogleCalendarUrl = (booking) => {
    if (!booking?.start_time) return null;
    const start = new Date(booking.start_time);
    const end = new Date(start.getTime() + 30 * 60000);
    const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const title = encodeURIComponent(`Booking with ${booking.provider_name || 'Provider'}`);
    const details = encodeURIComponent(booking.notes || 'GradLink booking');
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(start)}/${fmt(end)}&details=${details}`;
  };

  const statusColor = (status) => {
    const map = {
      pending: 'bg-amber-50 text-amber-700 border-amber-200',
      confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      cancelled: 'bg-red-50 text-red-700 border-red-200'
    };
    return map[status] || map.pending;
  };

  const myBookings = bookings.filter(b => b.client_id === user?.user_id);
  const providerBookings = bookings.filter(b => b.client_id !== user?.user_id);

  const filteredProviders = providers.filter(p => {
    if (selectedServiceType !== 'all' && p.category !== selectedServiceType) return false;
    if (providerSearch && !p.name?.toLowerCase().includes(providerSearch.toLowerCase()) &&
        !p.category?.toLowerCase().includes(providerSearch.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dark-slate"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="bookings-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-extrabold text-3xl text-dark-slate mb-1">Bookings</h1>
            <p className="text-slate-gray">Schedule consultations with service providers</p>
          </div>
          <Button
            onClick={() => setActiveTab('book-new')}
            className="bg-dark-slate text-white rounded-xl"
            data-testid="book-new-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Book Consultation
          </Button>
        </div>

        {/* Service Areas Grid - Always Visible */}
        <div className="mb-6">
          <h2 className="font-heading font-bold text-lg text-dark-slate mb-3">Service Areas</h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
            {allServiceAreas.map(sa => {
              const Icon = sa.icon;
              return (
                <button
                  key={sa.value}
                  onClick={() => handleServiceAreaClick(sa.value)}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all hover:shadow-md ${
                    selectedServiceType === sa.value
                      ? 'border-dark-slate bg-dark-slate text-white shadow-md'
                      : `border-slate-100 ${sa.color} hover:border-slate-200`
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[10px] sm:text-xs font-medium text-center leading-tight">{sa.label}</span>
                </button>
              );
            })}
            {/* Add Service Button */}
            <button
              onClick={() => setShowSuggestDialog(true)}
              className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-dashed border-slate-300 text-slate-400 hover:border-slate-400 hover:text-slate-500 transition-all"
            >
              <PlusCircle className="w-5 h-5" />
              <span className="text-[10px] sm:text-xs font-medium text-center leading-tight">Add Service</span>
            </button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 w-full max-w-lg mb-6 bg-slate-100 rounded-xl p-1">
            <TabsTrigger value="my-bookings" className="rounded-lg data-[state=active]:bg-white" data-testid="tab-my-bookings">
              My Bookings
            </TabsTrigger>
            <TabsTrigger value="requests" className="rounded-lg data-[state=active]:bg-white" data-testid="tab-requests">
              Requests
            </TabsTrigger>
            <TabsTrigger value="book-new" className="rounded-lg data-[state=active]:bg-white" data-testid="tab-book-new">
              Book New
            </TabsTrigger>
          </TabsList>

          {/* MY BOOKINGS */}
          <TabsContent value="my-bookings">
            <div className="space-y-3">
              {myBookings.length === 0 ? (
                <div className="text-center py-12">
                  <CalendarDays className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-gray">No bookings yet</p>
                  <Button onClick={() => setActiveTab('book-new')} variant="outline" className="mt-4 rounded-xl">
                    Book a Consultation
                  </Button>
                </div>
              ) : (
                myBookings.map(b => (
                  <BookingCard key={b.booking_id} booking={b} isProvider={false} onUpdate={handleUpdateBooking}
                    statusColor={statusColor} getGoogleCalendarUrl={getGoogleCalendarUrl} />
                ))
              )}
            </div>
          </TabsContent>

          {/* INCOMING REQUESTS (as provider) */}
          <TabsContent value="requests">
            <div className="space-y-3">
              {providerBookings.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-gray">No incoming booking requests</p>
                </div>
              ) : (
                providerBookings.map(b => (
                  <BookingCard key={b.booking_id} booking={b} isProvider={true} onUpdate={handleUpdateBooking}
                    statusColor={statusColor} getGoogleCalendarUrl={getGoogleCalendarUrl} />
                ))
              )}
            </div>
          </TabsContent>

          {/* BOOK NEW */}
          <TabsContent value="book-new">
            <div className="space-y-4">
              {/* Service type selector */}
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {allServiceTypes.map(st => (
                  <button key={st.value} onClick={() => setSelectedServiceType(st.value)}
                    className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedServiceType === st.value
                        ? 'bg-dark-slate text-white'
                        : 'bg-white text-slate-gray border border-slate-200 hover:bg-slate-50'
                    }`}>
                    {st.label}
                  </button>
                ))}
              </div>

              <div className="relative max-w-lg">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
                <Input
                  placeholder="Search providers by name or category..."
                  value={providerSearch}
                  onChange={(e) => setProviderSearch(e.target.value)}
                  className="pl-10 rounded-xl"
                  data-testid="provider-search"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredProviders.map(p => (
                  <div key={p.provider_id} className="bg-white rounded-2xl p-5 border border-slate-100 card-hover">
                    <div className="flex items-center gap-3 mb-3">
                      <Avatar className="w-11 h-11">
                        <AvatarFallback className="bg-mint-fresh text-dark-slate">{p.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-dark-slate truncate">{p.name}</p>
                        <p className="text-xs text-slate-gray capitalize">{p.category?.replace(/_/g, ' ')}</p>
                      </div>
                      {p.verified && <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                    </div>
                    {p.description && <p className="text-sm text-slate-gray mb-3 line-clamp-2">{p.description}</p>}

                    {/* Calendly link if available */}
                    {p.calendly_link ? (
                      <div className="space-y-2">
                        <Button
                          onClick={() => handleSelectProvider(p)}
                          className="w-full bg-dark-slate text-white rounded-xl"
                          data-testid={`book-provider-${p.provider_id}`}
                        >
                          <CalendarDays className="w-4 h-4 mr-2" /> Book
                        </Button>
                        <a href={extUrl(p.calendly_link)} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" className="w-full rounded-xl text-xs">
                            <ExternalLink className="w-3 h-3 mr-1.5" />Open Calendly
                          </Button>
                        </a>
                      </div>
                    ) : (
                      <Button
                        onClick={() => handleSelectProvider(p)}
                        className="w-full bg-dark-slate text-white rounded-xl"
                        data-testid={`book-provider-${p.provider_id}`}
                      >
                        <CalendarDays className="w-4 h-4 mr-2" /> Book
                      </Button>
                    )}
                  </div>
                ))}
                {filteredProviders.length === 0 && (
                  <div className="col-span-full text-center py-12 text-slate-gray">
                    No providers found
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Booking Confirmation with Google Calendar */}
        <Dialog open={!!confirmedBooking} onOpenChange={() => setConfirmedBooking(null)}>
          <DialogContent className="sm:max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-600">
                <CheckCircle className="w-5 h-5" />Booking Confirmed!
              </DialogTitle>
            </DialogHeader>
            {confirmedBooking && (
              <div className="space-y-4">
                <p className="text-sm text-slate-gray">
                  Your booking with <strong>{confirmedBooking.provider_name}</strong> has been requested.
                  You'll be notified when it's confirmed.
                </p>
                <div className="bg-slate-50 rounded-xl p-3 text-sm">
                  <p className="font-medium text-dark-slate">
                    {confirmedBooking.start_time ? format(parseISO(confirmedBooking.start_time), 'EEEE, MMMM d, yyyy') : ''}
                  </p>
                  <p className="text-slate-gray">
                    {confirmedBooking.start_time ? format(parseISO(confirmedBooking.start_time), 'h:mm a') : ''} (30 min)
                  </p>
                </div>
                <div className="flex gap-3">
                  {getGoogleCalendarUrl(confirmedBooking) && (
                    <a href={getGoogleCalendarUrl(confirmedBooking)} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button variant="outline" className="w-full rounded-xl">
                        <CalendarDays className="w-4 h-4 mr-2" />Add to Google Calendar
                      </Button>
                    </a>
                  )}
                  <Button onClick={() => setConfirmedBooking(null)} className="bg-dark-slate text-white rounded-xl">
                    Done
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Suggest Service Dialog */}
        <Dialog open={showSuggestDialog} onOpenChange={setShowSuggestDialog}>
          <DialogContent className="sm:max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PlusCircle className="w-5 h-5 text-emerald-500" /> Suggest a Service
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-gray">
                Don't see the service you're looking for? Suggest it below and we'll add it to the directory.
                You can also become a provider for this service in the Service Directory.
              </p>
              <Input
                placeholder="e.g., Interior Designer, Accountant, Life Coach..."
                value={suggestedService}
                onChange={(e) => setSuggestedService(e.target.value)}
                className="rounded-xl"
                onKeyDown={(e) => e.key === 'Enter' && handleSuggestService()}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSuggestDialog(false)}>Cancel</Button>
              <Button
                onClick={handleSuggestService}
                disabled={!suggestedService.trim() || suggestingService}
                className="bg-dark-slate text-white"
              >
                {suggestingService ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding...</> : 'Add Service'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Booking Dialog */}
        <Dialog open={bookingDialog} onOpenChange={setBookingDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                Book with {selectedProvider?.name}
              </DialogTitle>
            </DialogHeader>

            {/* Calendly embed notice */}
            {selectedProvider?.calendly_link && (
              <div className="bg-blue-50 rounded-xl p-3 text-sm flex items-center gap-2">
                <ExternalLink className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <span className="text-blue-700">
                  This provider also accepts bookings via{' '}
                  <a href={extUrl(selectedProvider.calendly_link)} target="_blank" rel="noopener noreferrer"
                    className="underline font-medium">Calendly</a>
                </span>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-dark-slate mb-2">Select Date</p>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(d) => d && setSelectedDate(d)}
                  disabled={(date) => isBefore(date, new Date()) && !isToday(date)}
                  className="rounded-xl border border-slate-200 p-3"
                />
              </div>

              <div>
                <p className="text-sm font-medium text-dark-slate mb-2">
                  Available Times for {format(selectedDate, 'EEEE, MMM d')}
                </p>
                <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                  {getAvailableSlots().map(slot => (
                    <Button
                      key={slot}
                      variant={selectedTime === slot ? 'default' : 'outline'}
                      size="sm"
                      className={`rounded-lg ${selectedTime === slot ? 'bg-dark-slate text-white' : ''}`}
                      onClick={() => setSelectedTime(slot)}
                      data-testid={`time-slot-${slot}`}
                    >
                      {slot}
                    </Button>
                  ))}
                  {getAvailableSlots().length === 0 && (
                    <p className="col-span-4 text-sm text-slate-gray text-center py-4">No available slots this day</p>
                  )}
                </div>
              </div>

              <Select value={bookingType} onValueChange={setBookingType}>
                <SelectTrigger data-testid="booking-type-select">
                  <SelectValue placeholder="Consultation type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General Consultation</SelectItem>
                  <SelectItem value="initial">Initial Assessment</SelectItem>
                  <SelectItem value="followup">Follow-up</SelectItem>
                  <SelectItem value="premium">Premium Session</SelectItem>
                </SelectContent>
              </Select>

              <Textarea
                placeholder="Notes for the provider (optional)..."
                value={bookingNotes}
                onChange={(e) => setBookingNotes(e.target.value)}
                data-testid="booking-notes"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBookingDialog(false)}>Cancel</Button>
              <Button
                onClick={handleBook}
                disabled={!selectedTime || creating}
                className="bg-dark-slate text-white"
                data-testid="confirm-booking"
              >
                {creating ? 'Booking...' : 'Confirm Booking'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

const BookingCard = ({ booking, isProvider, onUpdate, statusColor, getGoogleCalendarUrl }) => {
  const b = booking;
  const dateStr = b.start_time ? format(parseISO(b.start_time), 'EEE, MMM d') : '';
  const timeStr = b.start_time ? format(parseISO(b.start_time), 'h:mm a') : '';
  const gcalUrl = getGoogleCalendarUrl?.(b);

  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 flex items-center justify-between" data-testid={`booking-${b.booking_id}`}>
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl bg-mint-fresh/50 flex flex-col items-center justify-center">
          <CalendarDays className="w-5 h-5 text-dark-slate" />
        </div>
        <div>
          <p className="font-medium text-dark-slate">
            {isProvider ? b.client_name : b.provider_name}
          </p>
          <p className="text-sm text-slate-gray">{dateStr} at {timeStr}</p>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className={statusColor(b.status)}>{b.status}</Badge>
            <Badge variant="outline" className="text-xs capitalize">{b.consultation_type}</Badge>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {b.status === 'confirmed' && gcalUrl && (
          <a href={gcalUrl} target="_blank" rel="noopener noreferrer">
            <Button size="sm" variant="outline" className="rounded-lg text-xs">
              <CalendarDays className="w-3 h-3 mr-1" />GCal
            </Button>
          </a>
        )}
        {b.status === 'pending' && (
          <div className="flex gap-2">
            {isProvider && (
              <Button size="sm" onClick={() => onUpdate(b.booking_id, 'confirmed')} className="bg-emerald-600 text-white rounded-lg" data-testid={`confirm-${b.booking_id}`}>
                <CheckCircle className="w-4 h-4 mr-1" /> Confirm
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onUpdate(b.booking_id, 'cancelled')} className="rounded-lg" data-testid={`cancel-${b.booking_id}`}>
              <XCircle className="w-4 h-4 mr-1" /> Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Bookings;
